/** The accounts module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiDelete, apiDeleteWithResult, apiGet, apiPatch, apiPost, apiPut } from '../core/index.js'
import type { AcceptedSession, AcceptInvitationInput, Invitation, InvitationPreview, InvitationResult, InviteFacilityAdministratorInput, InviteOperatorInput, CreateFacilityAdministratorInput, DemoPeople, DemoSessionResponse, FacilityAdministrator, FacilityAdministratorWithPassword, Me, OpenDemoSessionInput, ResetLink, UpdateFacilityAdministratorInput, UpdateFacilityAdministratorResult, StaffNotificationSettings, StaffNotificationSettingsInput } from './types.js'

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet<Me>('/auth/me'),
  })
}

/**
 * `enabled` defaults to true but should be passed as false for a facility
 * administrator session - this list endpoint requires the facilities:manage
 * scope only an operator's token carries, so a facility administrator's
 * request would just 401.
 */
export function useFacilityAdministrators(clientId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['facility-administrators', clientId],
    queryFn: () => apiGet<FacilityAdministrator[]>(`/admin/clients/${clientId}/facility-administrators`),
    enabled: Boolean(clientId) && enabled,
  })
}

export function useCreateFacilityAdministrator(clientId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateFacilityAdministratorInput) =>
      apiPost<FacilityAdministratorWithPassword>(`/admin/clients/${clientId}/facility-administrators`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-administrators', clientId] })
    },
  })
}

export function useUpdateFacilityAdministrator(clientId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ administratorId, input }: { administratorId: string; input: UpdateFacilityAdministratorInput }) =>
      apiPatch<UpdateFacilityAdministratorResult>(`/admin/clients/${clientId}/facility-administrators/${administratorId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-administrators', clientId] })
    },
  })
}

export function useDeleteFacilityAdministrator(clientId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (administratorId: string) => apiDelete(`/admin/clients/${clientId}/facility-administrators/${administratorId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facility-administrators', clientId] })
    },
  })
}

/** Generates a one-time, admin-mediated forgot-password link - there's no email delivery, so the caller relays resetUrl to the administrator directly. */
export function useGenerateFacilityAdministratorResetLink(clientId: string | undefined) {
  return useMutation({
    mutationFn: (administratorId: string) =>
      apiPost<ResetLink>(`/admin/clients/${clientId}/facility-administrators/${administratorId}/reset-link`, {}),
  })
}

/** Who this session could open on its demo client: an operator's or facility administrator's own reach, or a demo session's opener's. Fetched when the chooser opens. */
export function useDemoPeople(enabled: boolean) {
  // Staff and the list of facilities only: a large client's workers are asked for a facility at a time (fetchDemoWorkers).
  return useQuery({ queryKey: ['demo-people'], queryFn: () => apiGet<DemoPeople>('/auth/demo-sessions/people?workers=none'), enabled })
}

/** One facility's workers the signed-in person could open, for the chooser to show when that facility is opened. Not a hook: the chooser owns the query. */
export function fetchDemoWorkers(facilityId: string): Promise<DemoPeople> {
  return apiGet<DemoPeople>(`/auth/demo-sessions/people?facilityId=${encodeURIComponent(facilityId)}`)
}

/** The workers, at any facility the signed-in person could open, whose name or id holds the text: how the chooser finds a worker without opening every facility. */
export function searchDemoWorkers(query: string): Promise<DemoPeople> {
  return apiGet<DemoPeople>(`/auth/demo-sessions/people?q=${encodeURIComponent(query)}`)
}

/** An operator or facility administrator of a demo client opens a session as someone below them (see api/demoSession.ts for the hand-off). */
export function useOpenDemoSession() {
  return useMutation({
    mutationFn: (input: OpenDemoSessionInput) => apiPost<DemoSessionResponse>('/auth/demo-sessions', input),
  })
}

/** A demo session asking for another hour as the same person; the answer is the new token for this tab. */
export function useExtendDemoSession() {
  return useMutation({
    mutationFn: () => apiPost<DemoSessionResponse>('/auth/demo-sessions/extend', {}),
  })
}

/** What an invitation link opens onto (invitations plan 3.4); public, the token is the proof. */
export function fetchInvitationPreview(token: string): Promise<InvitationPreview> {
  return apiGet<InvitationPreview>(`/auth/invitations?token=${encodeURIComponent(token)}`)
}

/** Accepts an invitation - by the link's token, or the email and the code - setting the password; answers a session for the right shell. */
export function acceptInvitation(input: AcceptInvitationInput): Promise<AcceptedSession> {
  return apiPost<AcceptedSession>('/auth/invitations/accept', input)
}

/** The invitations a client sent to a kind of login, newest first (invitations plan phase 2); the operator's own session reads them. */
export function useLoginInvitations(clientId: string | undefined, kind: 'operators' | 'facility-administrators', enabled = true) {
  return useQuery({
    queryKey: ['login-invitations', clientId, kind],
    queryFn: () => apiGet<Invitation[]>(`/admin/clients/${clientId}/${kind}/invitations`),
    enabled: Boolean(clientId) && enabled,
  })
}

function invalidateLoginInvitations(queryClient: ReturnType<typeof useQueryClient>, clientId: string | undefined, kind: 'operators' | 'facility-administrators'): void {
  void queryClient.invalidateQueries({ queryKey: ['login-invitations', clientId, kind] })
  void queryClient.invalidateQueries({ queryKey: [kind === 'operators' ? 'operators' : 'facility-administrators', clientId] })
}

/** An operator invites a fellow operator (D4). */
export function useInviteOperator(clientId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteOperatorInput) => apiPost<InvitationResult>(`/admin/clients/${clientId}/operators/invitations`, input),
    onSuccess: () => invalidateLoginInvitations(queryClient, clientId, 'operators'),
  })
}

/** An operator invites a facility administrator to named facilities; the login is made at once, switched off. */
export function useInviteFacilityAdministrator(clientId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: InviteFacilityAdministratorInput) => apiPost<InvitationResult>(`/admin/clients/${clientId}/facility-administrators/invitations`, input),
    onSuccess: () => invalidateLoginInvitations(queryClient, clientId, 'facility-administrators'),
  })
}

/** Resend (new secrets, five minutes apart) or withdraw (the pending login goes with it) one of a client's invitations. */
export function useLoginInvitationActions(clientId: string | undefined, kind: 'operators' | 'facility-administrators') {
  const queryClient = useQueryClient()
  const resend = useMutation({
    mutationFn: (invitationId: string) => apiPost<InvitationResult>(`/admin/clients/${clientId}/${kind}/invitations/${encodeURIComponent(invitationId)}/resend`, {}),
    onSuccess: () => invalidateLoginInvitations(queryClient, clientId, kind),
  })
  const revoke = useMutation({
    mutationFn: (invitationId: string) => apiPost<Invitation | null>(`/admin/clients/${clientId}/${kind}/invitations/${encodeURIComponent(invitationId)}/revoke`, {}),
    onSuccess: () => invalidateLoginInvitations(queryClient, clientId, kind),
  })
  return { resend, revoke }
}

/** The signed-in staff member's own notification settings (facility operations dashboard plan §5). */
export function useStaffNotifications(enabled = true) {
  return useQuery({ queryKey: ['staff-notifications'], queryFn: () => apiGet<StaffNotificationSettings>('/auth/me/notifications'), enabled })
}

/** Save the settings, add a mobile (a code is texted), verify it, or forget it. */
export function useStaffNotificationActions() {
  const queryClient = useQueryClient()
  const onSuccess = (settings: StaffNotificationSettings) => queryClient.setQueryData(['staff-notifications'], settings)
  return {
    save: useMutation({ mutationFn: (input: StaffNotificationSettingsInput) => apiPut<StaffNotificationSettings>('/auth/me/notifications', input), onSuccess }),
    addMobile: useMutation({ mutationFn: ({ mobile, wording }: { mobile: string; wording: string }) => apiPost<StaffNotificationSettings>('/auth/me/notifications/mobile', { mobile, consent: { wording } }), onSuccess }),
    verify: useMutation({ mutationFn: (code: string) => apiPost<StaffNotificationSettings>('/auth/me/notifications/mobile/verify', { code }), onSuccess }),
    removeMobile: useMutation({ mutationFn: () => apiDeleteWithResult<StaffNotificationSettings>('/auth/me/notifications/mobile'), onSuccess }),
  }
}
